import { DocumentNode, gql, useMutation } from '@apollo/client';
import {
  Checkbox,
  Hide,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useMediaQuery,
  useTheme,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd';

import { ResourceListItem } from '@/components/GroupPage/GroupResourcesPane/components/LibraryFileItem/components/ResourceListItem';
import { ResourceListFolder } from '@/components/GroupPage/GroupResourcesPane/components/LibraryFolderItem/ResourceListFolder/ResourceListFolder';
import { ResourceListLink } from '@/components/GroupPage/GroupResourcesPane/components/LibraryLinkItem/ResourceListLink/ResourceListLink';
import { TableLoading, TableNone } from '@/components/Table';
import {
  LibraryContentsItemType,
  ReorderGroupLibraryContentsInput,
} from '@/graphql-types/globalTypes';
import { Card } from '@/imports/ui/chakra/components/Card/Card';
import { ISelectionState } from '@/imports/ui/useSelectionState';

import {
  BrowseItemsList_libraryFolderResultsFragment_files_versionFile,
  BrowseItemsList_libraryFolderResultsFragment_folders,
  BrowseItemsList_libraryFolderResultsFragment_links,
} from './graphql-types/BrowseItemsList_libraryFolderResultsFragment';

type ILibraryFolder = BrowseItemsList_libraryFolderResultsFragment_folders;
type IVersionFile =
  BrowseItemsList_libraryFolderResultsFragment_files_versionFile;
type ILink = BrowseItemsList_libraryFolderResultsFragment_links;

export type IBrowseListItem =
  | { type: 'folder'; folder: ILibraryFolder }
  | { type: 'file'; versionFile: IVersionFile; fileLinkId: string }
  | { type: 'link'; link: ILink };

type IBrowseItemsListProps = {
  items: IBrowseListItem[];
  rootFolderId: string;
  libraryFolderId: string;
  selection: ISelectionState<{ _id: string }>;
  selectAll: () => void;
  selectNone: () => void;
  toggleOne: (item: { _id: string }) => void;
  navigateToFolder: (folderId: string) => void;
  currentUserCanEditGroupLibrary: boolean;
  groupId: string;
  loading: boolean;
  isManualOrder?: boolean;
};

const REORDER_GROUP_LIBRARY_CONTENTS = gql`
  mutation BrowseItemsList_reorderGroupLibraryContents(
    $input: ReorderGroupLibraryContentsInput!
  ) {
    reorderGroupLibraryContents(input: $input)
  }
`;

const getItemDragId = (item: IBrowseListItem): string => {
  if (item.type === 'folder') {
    return `folder-${item.folder._id}`;
  }
  if (item.type === 'link') {
    return `link-${item.link._id}`;
  }
  return `file-${item.fileLinkId}`;
};

const getItemReorderInput = (
  item: IBrowseListItem
): Pick<ReorderGroupLibraryContentsInput, 'itemId' | 'itemType'> => {
  if (item.type === 'folder') {
    return {
      itemId: item.folder._id,
      itemType: LibraryContentsItemType.FOLDER,
    };
  }
  if (item.type === 'link') {
    return {
      itemId: item.link._id,
      itemType: LibraryContentsItemType.LINK,
    };
  }
  return {
    itemId: item.fileLinkId,
    itemType: LibraryContentsItemType.FILE,
  };
};

export const BrowseItemsList: React.FC<IBrowseItemsListProps> & {
  libraryFolderResultsFragment: DocumentNode;
} = ({
  items,
  rootFolderId,
  libraryFolderId,
  selection,
  selectAll,
  selectNone,
  toggleOne,
  navigateToFolder,
  currentUserCanEditGroupLibrary,
  groupId,
  loading,
  isManualOrder = false,
}) => {
  const toast = useToast();
  const theme = useTheme();
  const lgBreakpoint = theme.breakpoints?.lg ?? '62em';
  const [isBelowLg] = useMediaQuery(`(max-width: ${lgBreakpoint})`);
  const columnCount =
    (currentUserCanEditGroupLibrary ? 2 : 0) + 1 + (isBelowLg ? 0 : 2) + 1;
  const hasFiles = items.some((item) => item.type === 'file');
  const canDrag = isManualOrder && currentUserCanEditGroupLibrary;
  const [optimisticItems, setOptimisticItems] = React.useState<
    IBrowseListItem[] | null
  >(null);
  const isReorderingRef = React.useRef(false);
  const preDragOrderKeyRef = React.useRef<string | null>(null);
  const displayItems = optimisticItems ?? items;
  const [reorderContents] = useMutation(REORDER_GROUP_LIBRARY_CONTENTS, {
    refetchQueries: ['GroupResourcesPane_LibraryFolderContents'],
    awaitRefetchQueries: true,
  });

  const itemsOrderKey = items.map(getItemDragId).join('|');

  React.useEffect(() => {
    if (!optimisticItems || !preDragOrderKeyRef.current) {
      return;
    }

    if (itemsOrderKey !== preDragOrderKeyRef.current) {
      preDragOrderKeyRef.current = null;
      setOptimisticItems(null);
    }
  }, [itemsOrderKey, optimisticItems]);

  const handleDragEnd = async (result: DropResult): Promise<void> => {
    if (
      isReorderingRef.current ||
      !result.destination ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const movedItem = displayItems[result.source.index];
    if (!movedItem) {
      return;
    }

    const nextItems = [...displayItems];
    const [removed] = nextItems.splice(result.source.index, 1);
    nextItems.splice(result.destination.index, 0, removed);
    isReorderingRef.current = true;
    preDragOrderKeyRef.current = itemsOrderKey;
    setOptimisticItems(nextItems);

    try {
      await reorderContents({
        variables: {
          input: {
            groupId,
            libraryFolderId:
              libraryFolderId === rootFolderId ? null : libraryFolderId,
            ...getItemReorderInput(movedItem),
            fromPosition: result.source.index + 1,
            toPosition: result.destination.index + 1,
          },
        },
      });
    } catch (err) {
      preDragOrderKeyRef.current = null;
      setOptimisticItems(null);
      toast({
        status: 'error',
        description: 'Failed to save the new order.',
      });
    } finally {
      isReorderingRef.current = false;
    }
  };

  const renderRow = (item: IBrowseListItem, index: number) => {
    const row =
      item.type === 'folder' ? (
        <ResourceListFolder
          key={`folder-${item.folder._id}`}
          folder={item.folder}
          rootFolderId={rootFolderId}
          navigateToFolder={navigateToFolder}
          onAfterMove={selectNone}
          currentUserCanEditLibrary={currentUserCanEditGroupLibrary}
          isReorderEnabled={canDrag}
        />
      ) : item.type === 'link' ? (
        <ResourceListLink
          key={`link-${item.link._id}`}
          link={item.link}
          canEditGroupLibrary={currentUserCanEditGroupLibrary}
          groupId={groupId}
          rootFolderId={rootFolderId}
          onAfterMove={selectNone}
          onAfterDelete={selectNone}
          onAfterRename={selectNone}
          isReorderEnabled={canDrag}
        />
      ) : (
        <ResourceListItem
          key={`file-${item.fileLinkId}`}
          versionFile={item.versionFile}
          selection={selection}
          toggleOne={toggleOne}
          canEditGroupLibrary={currentUserCanEditGroupLibrary}
          groupId={groupId}
          rootFolderId={rootFolderId}
          onAfterMove={selectNone}
          onAfterDelete={selectNone}
          isReorderEnabled={canDrag}
        />
      );

    if (!canDrag) {
      return row;
    }

    return (
      <Draggable
        key={getItemDragId(item)}
        draggableId={getItemDragId(item)}
        index={index}
      >
        {(provided, snapshot) => {
          if (item.type === 'folder') {
            return (
              <ResourceListFolder
                folder={item.folder}
                rootFolderId={rootFolderId}
                navigateToFolder={navigateToFolder}
                onAfterMove={selectNone}
                currentUserCanEditLibrary={currentUserCanEditGroupLibrary}
                isReorderEnabled={canDrag}
                dragOptions={{ provided, snapshot }}
              />
            );
          }
          if (item.type === 'link') {
            return (
              <ResourceListLink
                link={item.link}
                canEditGroupLibrary={currentUserCanEditGroupLibrary}
                groupId={groupId}
                rootFolderId={rootFolderId}
                onAfterMove={selectNone}
                onAfterDelete={selectNone}
                onAfterRename={selectNone}
                isReorderEnabled={canDrag}
                dragOptions={{ provided, snapshot }}
              />
            );
          }
          return (
            <ResourceListItem
              versionFile={item.versionFile}
              selection={selection}
              toggleOne={toggleOne}
              canEditGroupLibrary={currentUserCanEditGroupLibrary}
              groupId={groupId}
              rootFolderId={rootFolderId}
              onAfterMove={selectNone}
              onAfterDelete={selectNone}
              isReorderEnabled={canDrag}
              dragOptions={{ provided, snapshot }}
            />
          );
        }}
      </Draggable>
    );
  };

  const tableBody = loading ? (
    <TableLoading columns={4} />
  ) : displayItems.length ? (
    canDrag ? (
      <Droppable droppableId="group-library-contents">
        {(provided) => (
          <Tbody ref={provided.innerRef} {...provided.droppableProps}>
            {displayItems.map((item, index) => renderRow(item, index))}
            {provided.placeholder}
          </Tbody>
        )}
      </Droppable>
    ) : (
      <Tbody>
        {displayItems.map((item, index) => renderRow(item, index))}
      </Tbody>
    )
  ) : (
    <Tbody>
      <Tr>
        <Td colSpan={columnCount}>
          <TableNone>No files, folders, or links in here yet.</TableNone>
        </Td>
      </Tr>
    </Tbody>
  );

  return (
    <Stack>
      <Card overflow="hidden">
        <TableContainer>
          <Table size="sm" colorScheme="gray" w="full" sx={{ tableLayout: 'fixed' }}>
            <Thead bg="gray.100">
              <Tr>
                {currentUserCanEditGroupLibrary && <Th w="8" pr="0" />}
                {currentUserCanEditGroupLibrary && (
                  <Th w="10" pr="0">
                    {hasFiles && (
                      <Checkbox
                        key="select-all"
                        isChecked={selection.allSelected}
                        isIndeterminate={
                          (!selection.allSelected &&
                            selection.includedItems.length > 0) ||
                          (selection.allSelected &&
                            selection.excludedItems.length > 0)
                        }
                        onChange={() =>
                          selection.allSelected ? selectNone() : selectAll()
                        }
                        boxSize={6}
                      />
                    )}
                  </Th>
                )}
                <Th>
                  <Text textStyle="detail">NAME</Text>
                </Th>
                <Hide below="lg">
                  <Th w="36" whiteSpace="nowrap">
                    <Text textStyle="detail">DATE</Text>
                  </Th>
                  <Th w="32" pl="2">
                    <Text textStyle="detail">CREATED BY</Text>
                  </Th>
                </Hide>
                <Th w="14" />
              </Tr>
            </Thead>
            {canDrag ? (
              <DragDropContext onDragEnd={handleDragEnd}>
                {tableBody}
              </DragDropContext>
            ) : (
              tableBody
            )}
          </Table>
        </TableContainer>
      </Card>
    </Stack>
  );
};

BrowseItemsList.libraryFolderResultsFragment = gql`
  fragment BrowseItemsList_libraryFolderResultsFragment on LibraryFolderContentsResult {
    folders {
      _id
      position
      ...ResourceListFolder_LibraryFolderFragment
    }
    files {
      _id
      position
      versionFile {
        ...ResourceListItem_VersionFileFragment
      }
    }
    links {
      _id
      position
      ...ResourceListLink_LinkFragment
    }
  }
  ${ResourceListFolder.folderFragment}
  ${ResourceListItem.versionFileFragment}
  ${ResourceListLink.linkFragment}
`;
